import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { COVER_VIDEO_SAMPLES, MAP_VIDEO_SAMPLES } from './catalog';
import { useCoverFormat } from './use-cover-format';
import { SAMPLE_DURATION_SECONDS, SAMPLE_SECTION_LABEL } from './settings';
import { HOMEPAGE_OPENING_SECONDS, HOMEPAGE_PLAYBACK_SECONDS, HOMEPAGE_VIDEO_SAMPLES } from './homepage-samples';
import './hero-map-preview.css';

const MAP_SEQUENCE = HOMEPAGE_VIDEO_SAMPLES;
const REMAINING_MAP_COUNT = MAP_VIDEO_SAMPLES.length - COVER_VIDEO_SAMPLES.length;

/** The same recorded journey continues through the map and sensor previews. */
export function HeroMapPreview() {
  const [selected, setSelected] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [failed, setFailed] = useState(false);
  const video = useRef<HTMLVideoElement>(null);
  const resume = useRef<{ time: number; playing: boolean } | null>({ time: HOMEPAGE_OPENING_SECONDS, playing: true });
  const format = useCoverFormat(() => {
    rememberPlayback();
    setFailed(false);
  });
  const map = MAP_SEQUENCE[selected]!;
  const sample = { ...map, ...map.variants[format] };
  const sectionLabel = map.id === 'night-hawk' ? 'Randy’s Donuts' : SAMPLE_SECTION_LABEL;
  const currentSource = useRef(sample.src);
  currentSource.current = sample.src;

  useEffect(() => {
    const player = video.current;
    if (player) {
      player.defaultMuted = true;
      player.muted = true;
      player.playsInline = true;
    }
    if (player && !player.hasAttribute('src')) {
      player.src = `${currentSource.current}#t=${HOMEPAGE_OPENING_SECONDS}`;
      player.load();
    }
    if (player) playVideo(player);
    return () => {
      if (player) { player.pause(); player.removeAttribute('src'); player.load(); }
    };
  }, []);

  function rememberPlayback() {
    const player = video.current;
    if (player) resume.current ??= { time: player.currentTime, playing: !player.paused && !player.ended };
  }

  function switchMap(index: number) {
    const player = video.current;
    if (!player || selected === index) return;
    resume.current = { time: HOMEPAGE_OPENING_SECONDS, playing: true };
    setFailed(false);
    setSelected(index);
  }

  function togglePlayback() {
    const player = video.current;
    if (!player) return;
    if (resume.current?.playing || playing || !player.paused) {
      if (resume.current) resume.current.playing = false;
      player.pause(); setPlaying(false);
    } else if (failed) {
      resume.current = { time: Math.max(HOMEPAGE_OPENING_SECONDS, player.currentTime), playing: true };
      setFailed(false); player.load();
    } else {
      if (player.ended) player.currentTime = HOMEPAGE_OPENING_SECONDS;
      if (resume.current) resume.current.playing = true;
      playVideo(player);
    }
  }

  function playVideo(player: HTMLVideoElement) {
    const source = player.getAttribute('src');
    if (!resume.current && (player.readyState < 3 || player.seeking || player.currentTime < HOMEPAGE_OPENING_SECONDS - .05)) {
      resume.current = { time: Math.max(HOMEPAGE_OPENING_SECONDS, player.currentTime), playing: true };
    }
    void player.play().catch(error => {
      if (video.current !== player || player.getAttribute('src') !== source || error?.name === 'AbortError') return;
      resume.current = null;
      setPlaying(false);
      if (error?.name !== 'NotAllowedError') setFailed(true);
    });
  }

  function restorePlayback() {
    const previous = resume.current;
    const player = video.current!;
    const duration = Number.isFinite(player.duration) ? player.duration : SAMPLE_DURATION_SECONDS;
    const time = Math.max(HOMEPAGE_OPENING_SECONDS, Math.min(previous?.time ?? player.currentTime, duration));
    if (Math.abs(player.currentTime - time) > .05) {
      resume.current = previous ?? { time, playing: !player.paused && !player.ended };
      player.currentTime = time;
      return;
    }
    resume.current = null;
    if (previous?.playing) playVideo(player);
  }

  return <section className="hero-map-preview" aria-label="Recorded map video preview" data-cover-format={format}>
    <div className="hero-preview-film" style={{ '--hero-preview-aspect-ratio': sample.output.width / sample.output.height } as CSSProperties}>
      <video ref={video} data-public-sample controls playsInline preload="auto" muted autoPlay={resume.current?.playing ?? playing} loop width={sample.output.width} height={sample.output.height}
        src={`${sample.src}#t=${HOMEPAGE_OPENING_SECONDS}`} aria-label={`${sample.label} preview of the recorded ${sectionLabel}`}
        onLoadedData={restorePlayback} onSeeked={restorePlayback} onCanPlay={restorePlayback}
        onTimeUpdate={() => {
          const player = video.current;
          if (player && player.currentTime < HOMEPAGE_OPENING_SECONDS - .05 && !resume.current && !player.seeking) {
            resume.current = { time: HOMEPAGE_OPENING_SECONDS, playing };
            player.currentTime = HOMEPAGE_OPENING_SECONDS;
            return;
          }
        }}
        onPlay={() => {
          document.querySelectorAll<HTMLVideoElement>('video[data-public-sample]').forEach(other => { if (other !== video.current) other.pause(); });
          setPlaying(true); setFailed(false);
        }} onPause={() => setPlaying(false)} onError={() => { resume.current = null; setPlaying(false); setFailed(true); }}>
        <a href={sample.src}>Watch the {sample.label} map preview</a>
      </video>
      {failed && <p role="alert">The preview could not load. <a href={sample.src}>Open the video directly</a>.</p>}
    </div>
    <div className="hero-map-choices" role="group" aria-label="Homepage previews">
      {MAP_SEQUENCE.map((map, index) => <button key={map.id} type="button" aria-pressed={selected === index}
        onClick={() => { if (selected === index) togglePlayback(); else switchMap(index); }}>{map.label}</button>)}
      {REMAINING_MAP_COUNT > 0 && <a className="hero-map-more" href="/samples#maps">Plus {REMAINING_MAP_COUNT} more — with more on the way!</a>}
      <a className="hero-map-explore" href="/samples">Explore all maps, cameras, and formats →</a>
    </div>
    <div className="hero-preview-caption"><p>{sample.mapLabel} · {sample.cameraLabel} · {sectionLabel} · {HOMEPAGE_PLAYBACK_SECONDS} seconds</p>
    </div>
  </section>;
}
